import { 
    useState, createContext, useContext, ReactNode,
    Children, isValidElement, useEffect, useRef
} from "react";
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { useLocation, useNavigate } from "react-router-dom";
import { OAuth2DTO } from "../data";

export const AuthenticatedTemplate = ({ children }: { children: ReactNode }) => <>{children}</>;
export const UnauthenticatedTemplate = ({ children }: { children: ReactNode }) => <>{children}</>;

interface OAuth2ContextType {
    userManager: UserManager;
    loading: boolean;
}

interface OAuth2ProviderProps {
    children: ReactNode;
    oAuth2Config?: OAuth2DTO
}

interface AuthRedirectState {
    returnUrl: string;
}

const OAuth2Context = createContext<OAuth2ContextType>({ loading: false } as OAuth2ContextType);

export const OAuth2Provider = ({ children, oAuth2Config }: OAuth2ProviderProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [currentToken, setCurrentToken] = useState<string>(null);
    const [loading, setLoading] = useState(true);

    const callbackHandledRef = useRef<boolean>(false);
    const userManagerRef = useRef<UserManager | null>(null);

    let authenticatedChild: ReactNode = null;
    let unauthenticatedChild: ReactNode = null;

    Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;

        if (child.type === AuthenticatedTemplate) {
            authenticatedChild = child;
        } else if (child.type === UnauthenticatedTemplate) {
            unauthenticatedChild = child;
        }
    });

    if (!authenticatedChild || !unauthenticatedChild) {
        throw new Error(
            "LoginProvider richiede AuthenticatedTemplate, UnauthenticatedTemplate come children."
        );
    }

    if (!userManagerRef.current) {
        userManagerRef.current = new UserManager({
            authority: oAuth2Config?.authority,
            client_id: 'timesheet-client-web',
            redirect_uri: window.location.origin,
            post_logout_redirect_uri: `${window.location.origin}/`,
            response_type: 'code',
            scope: 'openid profile email offline_access Hyper.Timesheet',
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
        });
    }

    const userManager = userManagerRef.current;

    useEffect(() => {
        const isCallback = window.location.search.includes("code=") && window.location.search.includes("state=");

        if (isCallback) {
            if (callbackHandledRef.current) return;

            callbackHandledRef.current = true;

            userManager.signinRedirectCallback().then(user => {
                const state = user?.state as AuthRedirectState | undefined;
                const returnUrl = state?.returnUrl || sessionStorage.getItem('oidc:returnUrl') || '/home';
                
                sessionStorage.removeItem('oidc:returnUrl');
                setCurrentToken(user.access_token);

                if (!returnUrl.startsWith('/')) 
                    navigate('/home', { replace: true });
                else
                    navigate(returnUrl, { replace: true });
            }).finally(() => setLoading(false));

            return;
        }

        userManager.getUser().then(user => {
            if (!user || user.expired) {
                const returnUrl = window.location.pathname + window.location.search;
                sessionStorage.setItem('oidc:returnUrl', returnUrl);

                userManager.signinRedirect({
                    state: {
                        returnUrl: returnUrl
                    }
                });
            } else 
                setCurrentToken(user.access_token);
        }).finally(() => setLoading(false));
    });

    if (loading) return null;

    return (
        <OAuth2Context.Provider value={{ userManager: userManager!, loading }}>
            {currentToken && authenticatedChild}
            {!currentToken && unauthenticatedChild}
        </OAuth2Context.Provider>
    );
}

export const useOAuth2Context = () => {
    const context = useContext(OAuth2Context);
    if (!context)
        throw new Error("useOAuth2Context deve essere usato all'interno di <OAuth2Provider>");

    return context;
}